import { useState } from 'react'
import {
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
  Link,
  Alert,
  AlertIcon,
  AlertDescription,
  VStack,
  Card,
  CardBody,
  OrderedList,
  ListItem,
  Box,
  Flex,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  IconButton,
  Divider,
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { FiEye, FiEyeOff } from 'react-icons/fi'

const Login = () => {
  const [apiKey, setApiKey] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  
  const bgColor = useColorModeValue('gray.50', 'gray.900')
  const cardBgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textColor = useColorModeValue('gray.800', 'gray.100')
  const brandTextColor = useColorModeValue('brand.600', 'brand.300')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await login(apiKey)
      navigate('/')
    } catch (error) {
      toast({
        title: 'Authentication failed',
        description: 'Please check your API key and try again',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box minH="100vh" bg={bgColor} py={{ base: '12', md: '24' }} px={{ base: '4', sm: '8' }}>
      <Container maxW="lg">
        <Stack spacing="8">
          <Stack spacing="6" align="center">
            <Flex 
              bg="brand.500" 
              w="16" 
              h="16" 
              borderRadius="lg" 
              alignItems="center" 
              justifyContent="center"
              boxShadow="md"
            >
              <Text color="white" fontWeight="bold" fontSize="3xl">D</Text>
            </Flex>
            <Stack spacing="3" textAlign="center">
              <Heading size="xl" color={brandTextColor}>Dyno Manager</Heading>
              <Text color={textColor} fontSize="lg">
                Manage and schedule your Heroku dyno scaling operations
              </Text>
            </Stack>
          </Stack>

          <Card 
            boxShadow="xl" 
            borderRadius="xl" 
            bg={cardBgColor} 
            borderColor={borderColor} 
            borderWidth="1px"
          >
            <CardBody p={{ base: 6, md: 8 }}>
              <VStack spacing="6">
                <Alert status="info" borderRadius="md" variant="left-accent">
                  <AlertIcon />
                  <AlertDescription>
                    You'll need a Heroku API key to use this application
                  </AlertDescription>
                </Alert>

                <Box w="full">
                  <Divider my={4} />
                  <Text fontWeight="medium" mb={4} color={textColor}>
                    How to get your Heroku API key:
                  </Text>
                  <OrderedList spacing={3} fontSize="md" color={textColor} pl={4}>
                    <ListItem>Go to your <Link href="https://dashboard.heroku.com/account" isExternal color="brand.500" fontWeight="semibold">Heroku Account Settings</Link></ListItem>
                    <ListItem>Scroll to the "API Key" section</ListItem>
                    <ListItem>Click "Reveal" to see your existing API key or "Regenerate API Key" to create a new one</ListItem>
                    <ListItem>Copy the API key and paste it below</ListItem>
                  </OrderedList>
                  <Divider my={4} />
                </Box>

                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <Stack spacing="6">
                    <FormControl isRequired>
                      <FormLabel htmlFor="apiKey" fontSize="md" fontWeight="medium" color={brandTextColor}>Heroku API Key</FormLabel>
                      <InputGroup size="lg">
                        <Input
                          id="apiKey"
                          type={showApiKey ? 'text' : 'password'}
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          placeholder="Enter your Heroku API key"
                          autoComplete="off"
                          bg={useColorModeValue('gray.50', 'gray.700')}
                          borderColor={borderColor}
                          _hover={{
                            borderColor: 'brand.400',
                          }}
                          _focus={{
                            borderColor: 'brand.500',
                            boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                          }}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                            icon={showApiKey ? <FiEyeOff /> : <FiEye />}
                            variant="ghost"
                            onClick={() => setShowApiKey(!showApiKey)}
                            size="sm"
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>
                    <Button
                      type="submit"
                      colorScheme="brand"
                      size="lg"
                      fontSize="md"
                      fontWeight="bold"
                      isLoading={isLoading}
                      w="full"
                      py={6}
                    >
                      Sign in
                    </Button>
                  </Stack>
                </form>

                <Alert status="warning" borderRadius="md" variant="left-accent">
                  <AlertIcon />
                  <AlertDescription>
                    Your API key is stored securely in your browser and is only used to communicate with Heroku's API.
                  </AlertDescription>
                </Alert>
              </VStack>
            </CardBody>
          </Card>
        </Stack>
      </Container>
    </Box>
  )
}

export default Login 